import { fetchFxRate } from "../api";
import { CategoryLimits, ExpenseLineItem } from "../types";

export interface BillTotals {
  totalInRequestCurrency: number;
  /** How far the request-currency bills go over estimatedAllowanceAmount
   *  (0 when they don't). Only ever counts bills ALREADY in the request's
   *  own currency - see exceededByConverted below for the converted
   *  figure that counts every bill regardless of its own currency. */
  exceededBy: number;
  /** A plain total per other currency, sorted by currency code. */
  others: { currency: string; total: number }[];
}

/** One total per currency - mixing currencies into one number would be
 *  meaningless. Only the request's own currency has a ceiling to compare
 *  against (estimatedAllowanceAmount is resolved in that currency - see
 *  db/policy_store.py), so exceededBy is computed only for it.
 *
 *  Bills in a "No limit" category (null in limitsByCurrency) never exceed
 *  anything, and the estimate already leaves those categories out (see
 *  db/travel_duration.py's estimate_reimbursement_total). limitsByCurrency
 *  is a Reimbursement-only snapshot, so on Fixed Allowance every bill counts
 *  against the one flat allowance. Mirrored server-side by _bill_totals in
 *  reports/request_report.py. Stays purely synchronous/unconverted - used
 *  for plain per-currency display (MyRequests.tsx's Amount column,
 *  OtherReimbursements.tsx) where no live FX call belongs on every table
 *  row; see exceededByConverted below for the one caller that needs a
 *  cross-currency check (DetailCard.tsx's "Exceeds..." warning). */
export function billTotals(
  expenses: ExpenseLineItem[],
  currency: string,
  estimatedAllowanceAmount: number,
  limitsByCurrency?: Record<string, CategoryLimits> | null
): BillTotals {
  const sums = new Map<string, number>();
  let limitedInRequestCurrency = 0;
  const requestLimits = limitsByCurrency?.[currency];
  for (const e of expenses) {
    const cur = e.currency || currency;
    sums.set(cur, (sums.get(cur) || 0) + e.amount);
    if (cur === currency && requestLimits?.[e.category] !== null) {
      limitedInRequestCurrency += e.amount;
    }
  }
  const over = limitedInRequestCurrency - (estimatedAllowanceAmount || 0);
  return {
    totalInRequestCurrency: sums.get(currency) || 0,
    exceededBy: over > 0 ? over : 0,
    others: [...sums.keys()]
      .filter((c) => c !== currency)
      .sort()
      .map((cur) => ({ currency: cur, total: sums.get(cur) || 0 }))
  };
}

/** The converted equivalent of billTotals' own exceededBy: every bill,
 *  regardless of its own currency, converted into `currency` via
 *  Frankfurter (the bill's own transaction date - same convention the FX
 *  reconciliation ledger uses) before being summed and compared against
 *  estimatedAllowanceAmount. Confirmed desired behaviour (client
 *  requirement change, same one requests_api.py's
 *  _bill_amount_in_request_currency/_category_limit now follow
 *  server-side): a bill in another currency is no longer left out of this
 *  check just because it isn't in the request's own currency.
 *
 *  A bill whose currency has no FX rate available for its date is left out
 *  of the sum entirely (same fail-soft rule the server follows) - this
 *  never blocks or fails outright, it just can't count that one bill this
 *  time. Async (live FX calls), so call it from an effect, not inline on
 *  every render - see DetailCard.tsx's BillsSummaryCard for the pattern. */
export async function exceededByConverted(
  expenses: ExpenseLineItem[],
  currency: string,
  estimatedAllowanceAmount: number,
  limitsByCurrency?: Record<string, CategoryLimits> | null
): Promise<number> {
  const requestLimits = limitsByCurrency?.[currency];
  const amounts = await Promise.all(
    expenses.map(async (e) => {
      if (requestLimits?.[e.category] === null) return 0;
      const cur = e.currency || currency;
      if (cur === currency) return e.amount;
      const date = e.date ? e.date.slice(0, 10) : undefined;
      const rate = await fetchFxRate(cur, currency, date).catch(() => null);
      return rate === null ? 0 : e.amount * rate;
    })
  );
  const total = amounts.reduce((sum, a) => sum + a, 0);
  const over = total - (estimatedAllowanceAmount || 0);
  return over > 0 ? over : 0;
}
